import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiMenu, HiX, HiChevronDown } from 'react-icons/hi';
import { useNavbarContent } from '../../../hooks/useNavbarContent';
import { lockBodyScroll, unlockBodyScroll } from '../../../utils/bodyScrollLock';
import MegaMenu, { MegaMenuMobile } from './MegaMenu/MegaMenu';
import './Navbar.css';

const Navbar = () => {
  const { content, isLoading } = useNavbarContent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMegaMenu, setOpenMegaMenu] = useState(null);
  const [openMobileMegaMenu, setOpenMobileMegaMenu] = useState(null);
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsIntroComplete(true), 1100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!content || !isIntroComplete) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [content, isIntroComplete]);

  useEffect(() => {
    if (isMenuOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    const handleResize = () => {
      if (window.matchMedia('(min-width: 769px)').matches) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      unlockBodyScroll();
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenMegaMenu(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenMobileMegaMenu(null);
  };

  const openMega = (id) => setOpenMegaMenu(id);
  const closeMega = () => setOpenMegaMenu(null);
  const toggleMobileMega = (id) => {
    setOpenMobileMegaMenu((current) => (current === id ? null : id));
  };

  const { logo, links, cta, megaMenus } = content || {};

  return (
    <nav
      className={`navbar${isVisible ? ' navbar--visible' : ''}${isLoading ? ' navbar--loading' : ''}`}
      onMouseLeave={content ? closeMega : undefined}
      aria-hidden={!isVisible && !isMenuOpen ? 'true' : undefined}
    >
      <div className="navbar__inner">
        {content ? (
          <>
            <Link
              to="/"
              className="navbar__logo"
              aria-label="VBS home"
              onMouseEnter={closeMega}
            >
              <img src={logo.url} alt={logo.alt} width="67" height="32" />
            </Link>

            <ul className="navbar__links">
              {links.map(({ label, to, megaMenu }) => (
                <li key={to} onMouseEnter={() => (megaMenu ? openMega(megaMenu) : closeMega())}>
                  <Link
                    to={to}
                    className={`navbar__link ${
                      megaMenu && openMegaMenu === megaMenu ? 'navbar__link--open' : ''
                    }`}
                    aria-haspopup={megaMenu || undefined}
                    aria-expanded={megaMenu ? openMegaMenu === megaMenu : undefined}
                    onFocus={() => (megaMenu ? openMega(megaMenu) : closeMega())}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <Link
              to={cta.url}
              className="navbar__cta luxury-btn"
              onMouseEnter={closeMega}
            >
              {cta.text}
            </Link>

            <button
              type="button"
              className="navbar__toggle"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(true)}
            >
              <HiMenu />
            </button>
          </>
        ) : null}
      </div>

      {content &&
        Object.entries(megaMenus).map(([id, menu]) => (
          <MegaMenu
            key={id}
            menu={menu}
            isOpen={openMegaMenu === id}
            onMouseEnter={() => openMega(id)}
            onMouseLeave={closeMega}
            onNavigate={closeMega}
          />
        ))}

      {content && (
        <div className={`navbar__mobile ${isMenuOpen ? 'navbar__mobile--open' : ''}`}>
          <button
            type="button"
            className="navbar__mobile-backdrop"
            aria-label="Close menu"
            onClick={closeMenu}
          />

          <div className="navbar__mobile-panel">
            <div className="navbar__mobile-header">
              <Link to="/" className="navbar__logo" aria-label="VBS home" onClick={closeMenu}>
                <img src={logo.url} alt={logo.alt} width="67" height="32" />
              </Link>

              <button
                type="button"
                className="navbar__mobile-close"
                aria-label="Close menu"
                onClick={closeMenu}
              >
                <HiX />
              </button>
            </div>

            <div className="navbar__mobile-divider" />

            <ul className="navbar__mobile-links">
              {links.map(({ label, to, hasChevron, megaMenu }) =>
                megaMenu ? (
                  <li key={to}>
                    <button
                      type="button"
                      className={`navbar__mobile-link navbar__mobile-link--toggle ${
                        openMobileMegaMenu === megaMenu ? 'navbar__mobile-link--open' : ''
                      }`}
                      aria-expanded={openMobileMegaMenu === megaMenu}
                      onClick={() => toggleMobileMega(megaMenu)}
                    >
                      <span>{label}</span>
                      <HiChevronDown className="navbar__mobile-chevron" aria-hidden="true" />
                    </button>

                    <MegaMenuMobile
                      menu={megaMenus[megaMenu]}
                      isOpen={openMobileMegaMenu === megaMenu}
                      onNavigate={closeMenu}
                    />
                  </li>
                ) : (
                  <li key={to}>
                    <Link to={to} className="navbar__mobile-link" onClick={closeMenu}>
                      <span>{label}</span>
                      {hasChevron && (
                        <HiChevronDown className="navbar__mobile-chevron" aria-hidden="true" />
                      )}
                    </Link>
                  </li>
                )
              )}
            </ul>

            <div className="navbar__mobile-divider" />

            <div className="navbar__mobile-actions">
              <Link to={cta.url} className="navbar__mobile-cta" onClick={closeMenu}>
                {cta.text}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
